/**
 * #2114/#2115 — single-flight access-token refresh.
 *
 * One refresh request at a time, shared by every caller (apiFetch's 401
 * retry path, useAuth's expiry timer, the visibilitychange resume check).
 * Concurrent callers await the same promise, so the burst of 401s after iOS
 * resumes a suspended PWA produces exactly ONE POST /api/auth/token/refresh/.
 *
 * Failure classes (#2114) — the reason this module exists:
 *   - AUTH failure (HTTP 400/401/403 from the refresh endpoint): the refresh
 *     token is truly dead. Tokens are cleared, 'rdm:session-expired' fires
 *     (useAuth clears the rest and routes to login), and
 *     AuthSessionExpiredError is thrown.
 *   - TRANSIENT failure (network error, 5xx): tokens are KEPT and a plain
 *     error propagates. A dropped packet on field cellular must never log a
 *     rep out (diag #51 review).
 *
 * Events:
 *   - 'rdm:token-refreshed'  CustomEvent<{ access: string }> on success
 *   - 'rdm:session-expired'  Event when the session is definitively over
 */

const BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000';

export class AuthSessionExpiredError extends Error {
  constructor(message = 'Session expired — please log in again') {
    super(message);
    this.name = 'AuthSessionExpiredError';
  }
}

let inFlight: Promise<string> | null = null;

/** Refresh the access token, coalescing concurrent callers onto one request. */
export function refreshAccessToken(): Promise<string> {
  if (!inFlight) {
    inFlight = doRefresh().finally(() => {
      inFlight = null;
    });
  }
  return inFlight;
}

async function doRefresh(): Promise<string> {
  const refresh = localStorage.getItem('refreshToken');
  if (!refresh) {
    expireSession();
    throw new AuthSessionExpiredError('No refresh token available');
  }

  // A thrown fetch (offline, timeout, dropped connection) propagates
  // untouched — transient by definition, tokens stay put (#2114).
  const response = await fetch(`${BASE_URL}/api/auth/token/refresh/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ refresh }),
  });

  if (response.status === 400 || response.status === 401 || response.status === 403) {
    // The refresh token itself was rejected — the session is over.
    expireSession();
    throw new AuthSessionExpiredError();
  }
  if (!response.ok) {
    // 5xx / maintenance — transient. Keep tokens; callers retry later.
    throw new Error(`Token refresh failed: HTTP ${response.status}`);
  }

  const data = (await response.json()) as { access?: string; refresh?: string };
  if (!data.access) {
    // Defensive: a 200 without an access token is a server bug — treat as
    // transient rather than killing the session.
    throw new Error('Token refresh response missing access token');
  }

  localStorage.setItem('accessToken', data.access);
  if (data.refresh) {
    // SIMPLE_JWT rotates refresh tokens when ROTATE_REFRESH_TOKENS is on.
    localStorage.setItem('refreshToken', data.refresh);
  }
  window.dispatchEvent(new CustomEvent('rdm:token-refreshed', { detail: { access: data.access } }));
  return data.access;
}

function expireSession(): void {
  // Only the tokens — useAuth's session-expired listener sweeps the rest
  // (associate, permissions, cached user) via its clearAuth().
  localStorage.removeItem('accessToken');
  localStorage.removeItem('refreshToken');
  window.dispatchEvent(new Event('rdm:session-expired'));
}
